import { useMemo } from 'react';
// `phil-reg-prov-mun-brgy` is JS-only (ships no type declarations); we wrap it
// in the typed facade below. @ts-ignore (not expect-error) because some
// consumers' own ambient shims make this import already-typed.
// @ts-ignore -- no type declarations for this package
import philRaw from 'phil-reg-prov-mun-brgy';
import type { Address } from '@mlm/shared';
import { Input } from './input';

// `phil-reg-prov-mun-brgy` is JS-only; type a minimal facade here so the
// component is self-contained (consumers compiling this source don't need an
// ambient declaration of their own).
type PhilRegion = { name: string; reg_code: string };
type PhilProvince = { name: string; prov_code: string };
type PhilCityMun = { name: string; mun_code: string };
type PhilBarangay = { name: string; brgy_code?: string };
const phil = philRaw as {
  regions: PhilRegion[];
  getProvincesByRegion(regCode: string): PhilProvince[];
  getCityMunByProvince(provCode: string): PhilCityMun[];
  getBarangayByMun(munCode: string): PhilBarangay[];
};

/**
 * Shared PH address picker with cascading PSGC dropdowns
 * (Region → Province → City/Municipality → Barangay), sourced from
 * `phil-reg-prov-mun-brgy`. Used by the Ecom checkout AND the merchant FF
 * delivery-details block so they collect identical data. Each parent change
 * clears its descendants. Keyed on the shared `Address` type.
 */
export const PH_REGIONS: Array<{ code: string; name: string }> = [...phil.regions]
  .map((r) => ({ code: r.reg_code, name: r.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function regionNameForCode(code?: string): string | undefined {
  return PH_REGIONS.find((r) => r.code === code)?.name;
}

/** One-line summary of an address, for compact display. */
export function formatAddressLine(a: Address | null | undefined): string {
  if (!a) return '';
  const parts = [a.line1, a.barangay, a.city, a.province, regionNameForCode(a.regionCode) ?? a.region]
    .map((p) => p?.trim())
    .filter(Boolean);
  return parts.join(', ');
}

export function AddressFields({
  value,
  onChange,
  requireContactNo,
  /** Show the merchant FF extras: delivery contact person + free-text full address. */
  showDeliveryExtras,
  /** Mark EVERY field required (asterisk on each label). Enforcement stays with
   * the caller; this only renders the markers. Used by merchant registration. */
  requireAll,
}: {
  value: Address;
  onChange: (next: Address) => void;
  requireContactNo?: boolean;
  showDeliveryExtras?: boolean;
  requireAll?: boolean;
}) {
  const provinces = useMemo(
    () =>
      value.regionCode
        ? phil
            .getProvincesByRegion(value.regionCode)
            .map((p) => ({ code: p.prov_code, name: p.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [value.regionCode],
  );
  const cities = useMemo(
    () =>
      value.provinceCode
        ? phil
            .getCityMunByProvince(value.provinceCode)
            .map((c) => ({ code: c.mun_code, name: c.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [value.provinceCode],
  );
  const barangays = useMemo(
    () =>
      value.cityCode
        ? phil
            .getBarangayByMun(value.cityCode)
            .map((b, i) => ({ code: b.brgy_code ?? `${value.cityCode}-${i}`, name: b.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [value.cityCode],
  );

  function setRegion(code: string) {
    onChange({
      ...value,
      regionCode: code,
      region: PH_REGIONS.find((r) => r.code === code)?.name,
      provinceCode: undefined,
      province: undefined,
      cityCode: undefined,
      city: undefined,
      barangayCode: undefined,
      barangay: undefined,
    });
  }
  function setProvince(code: string) {
    onChange({
      ...value,
      provinceCode: code,
      province: provinces.find((p) => p.code === code)?.name,
      cityCode: undefined,
      city: undefined,
      barangayCode: undefined,
      barangay: undefined,
    });
  }
  function setCity(code: string) {
    onChange({
      ...value,
      cityCode: code,
      city: cities.find((c) => c.code === code)?.name,
      barangayCode: undefined,
      barangay: undefined,
    });
  }
  function setBarangay(code: string) {
    onChange({
      ...value,
      barangayCode: code,
      barangay: barangays.find((b) => b.code === code)?.name,
    });
  }

  const set = (k: keyof Address, v: string) => onChange({ ...value, [k]: v });

  return (
    <div className="space-y-3">
      <Field label="Address line 1" required={requireAll}>
        <Input value={value.line1 ?? ''} onChange={(e) => set('line1', e.target.value)} />
      </Field>
      <Field label="Address line 2" required={requireAll}>
        <Input value={value.line2 ?? ''} onChange={(e) => set('line2', e.target.value)} />
      </Field>
      <Field label="Region" required={requireAll}>
        <Select
          value={value.regionCode ?? ''}
          options={PH_REGIONS}
          placeholder="Select region…"
          onChange={setRegion}
        />
      </Field>
      <Field label="Province" required={requireAll}>
        <Select
          value={value.provinceCode ?? ''}
          options={provinces}
          placeholder={value.regionCode ? 'Select province…' : 'Pick a region first'}
          onChange={setProvince}
          disabled={!value.regionCode}
        />
      </Field>
      <Field label="City / Municipality" required={requireAll}>
        <Select
          value={value.cityCode ?? ''}
          options={cities}
          placeholder={value.provinceCode ? 'Select city…' : 'Pick a province first'}
          onChange={setCity}
          disabled={!value.provinceCode}
        />
      </Field>
      <Field label="Barangay" required={requireAll}>
        <Select
          value={value.barangayCode ?? ''}
          options={barangays}
          placeholder={value.cityCode ? 'Select barangay…' : 'Pick a city first'}
          onChange={setBarangay}
          disabled={!value.cityCode}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Postal code" required={requireAll}>
          <Input value={value.postalCode ?? ''} onChange={(e) => set('postalCode', e.target.value)} />
        </Field>
        <Field label="Delivery contact #" required={requireAll || requireContactNo}>
          <Input
            value={value.contactNo ?? ''}
            onChange={(e) => set('contactNo', e.target.value)}
            placeholder={requireAll || requireContactNo ? 'Required for delivery' : undefined}
          />
        </Field>
      </div>
      {showDeliveryExtras && (
        <>
          <Field label="Delivery contact person (who the rider asks for)" required={requireAll}>
            <Input
              value={value.contactPerson ?? ''}
              onChange={(e) => set('contactPerson', e.target.value)}
            />
          </Field>
          <Field label="Full address (free text)" required={requireAll}>
            <textarea
              className="w-full min-h-[60px] rounded-md border bg-background px-2 py-1.5 text-sm"
              value={value.fullAddress ?? ''}
              onChange={(e) => set('fullAddress', e.target.value)}
              placeholder="Type the complete address as it should appear on the parcel"
            />
          </Field>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      {children}
    </div>
  );
}

function Select({
  value,
  options,
  placeholder,
  onChange,
  disabled,
}: {
  value: string;
  options: Array<{ code: string; name: string }>;
  placeholder: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      className="w-full h-9 rounded-md border bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.code} value={o.code}>
          {o.name}
        </option>
      ))}
    </select>
  );
}
